import {
  Box,
  Flex,
  Heading,
  Image,
  Link,
  List,
  Text,
  Separator,
  Icon,
  HStack,
  type LinkProps,
} from "@chakra-ui/react";
import type { ElementType } from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import { FaFacebookF, FaInstagram, FaTiktok } from "react-icons/fa6";
import { Link as RouterLink } from "react-router-dom";
import { motion, type Variants } from "framer-motion";
import { useEffect, useState } from "react";

const MotionFlex = motion.create(Flex);
const MotionBox = motion.create(Box);

const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

const colVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease } },
};

const bottomVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease, delay: 0.1 } },
};

export default function Footer() {
  const [stickyVisible, setStickyVisible] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const { visible } = (e as CustomEvent<{ visible: boolean }>).detail;
      if (!visible) { setStickyVisible(false); return; }
      setTimeout(() => {
        const el = document.getElementById("sticky-cta-bar");
        setStickyVisible(el ? getComputedStyle(el).display !== "none" : false);
      }, 50);
    };
    window.addEventListener("sticky-cta-change", handler);
    return () => window.removeEventListener("sticky-cta-change", handler);
  }, []);

  return (
    <Box w="100%" bg="greyscale.surface.default" pb={{ base: stickyVisible ? "5rem" : 0, tablet: 0 }}>
      <Flex
        justify="center"
        py={{ base: "2.5rem", tablet: "3.25rem", desktop: "4.25rem" }}
      >
        <Flex
          w="full"
          maxW="1512px"
          flexDir="column"
          px={{ base: "1.25rem", tablet: "2rem", desktop: "5rem" }}
        >
          {/* Top Grid */}
          <MotionBox
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            w="full"
            display="grid"
            gridTemplateColumns={{
              base: "repeat(2, 1fr)",
              tablet: "repeat(4, minmax(0, 1fr))",
              desktop: "minmax(280px, 1.2fr) repeat(3, minmax(0, 1fr))",
            }}
            columnGap={{ base: "1.25rem", tablet: "1.25rem", desktop: "3rem" }}
            rowGap={{ base: "2.75rem", tablet: "2.25rem", desktop: "1.5rem" }}
          >
            {/* Brand */}
            <MotionFlex
              variants={colVariants}
              flexDir="column"
              align="flex-start"
              textAlign="left"
              gap="1rem"
              pr={{ desktop: "1rem" }}
            >
              <Image
                src="/home/hhLogo2.png"
                w={{ base: "11.5rem", tablet: "13rem", desktop: "14rem" }}
                alt="HomeHelpy logo"
              />

              <Text
                textStyle="bodySmall.base"
                color="greyscale.text.body"
                maxW={{ base: "100%", tablet: "30rem", desktop: "20rem" }}
                lineHeight="1.75"
              >
                A home service platform offering quality cleaning, aircon
                maintenance, and customer care to keep your home spotless and
                stress-free.
              </Text>

              <HStack gap="1.25rem" justify="flex-start" pt="0.25rem">
                <SocialLink
                  href="https://www.facebook.com/share/1GNwsVYxdL/"
                  icon={FaFacebookF}
                  label="Facebook"
                />
                <SocialLink
                  href="https://www.instagram.com/homehelpy?igsh=MWtzNzJiNzR4NTFnZA=="
                  icon={FaInstagram}
                  label="Instagram"
                />
                <SocialLink
                  href="https://www.tiktok.com/@homehelpy?is_from_webapp=1&sender_device=pc"
                  icon={FaTiktok}
                  label="TikTok"
                />
              </HStack>
            </MotionFlex>

            {/* Quick Links */}
            <MotionBox variants={colVariants}>
              <FooterSection title="Quick Links">
                <List.Item>
                  <StyledFooterLink to="/">Home</StyledFooterLink>
                </List.Item>
                <List.Item>
                  <StyledFooterLink to="/packages">Packages</StyledFooterLink>
                </List.Item>
                <List.Item>
                  <StyledFooterLink to="/faq">FAQ</StyledFooterLink>
                </List.Item>
                <List.Item>
                  <StyledFooterLink to="/#why-homehelpy">
                    Why Choose Us
                  </StyledFooterLink>
                </List.Item>
              </FooterSection>
            </MotionBox>

            {/* Services */}
            <MotionBox variants={colVariants}>
              <FooterSection title="Our Services">
                <List.Item>
                  <StyledFooterLink to="/services/classic-cleaning">
                    General Cleaning
                  </StyledFooterLink>
                </List.Item>
                <List.Item>
                  <StyledFooterLink to="/services/aircon-servicing">
                    Aircon Servicing
                  </StyledFooterLink>
                </List.Item>
                <List.Item>
                  <StyledFooterLink to="/services/deep-cleaning">
                    Deep Cleaning
                  </StyledFooterLink>
                </List.Item>
                <List.Item>
                  <StyledFooterLink to="/services/disinfection">
                    Disinfection
                  </StyledFooterLink>
                </List.Item>
                <List.Item>
                  <StyledFooterLink to="/services/office-cleaning">
                    Office Cleaning
                  </StyledFooterLink>
                </List.Item>
              </FooterSection>
            </MotionBox>

            {/* Contact */}
            <MotionBox variants={colVariants}>
              <FooterSection title="Contact Us">
                <FooterContact icon={Phone} text="+65 8938 8171" />
                <FooterContact
                  icon={MapPin}
                  text={"60 MacPherson Road, Siemens Centre\n#04-08 Singapore 348615"}
                />
                <FooterContact icon={Mail} text="enquiries@homehelpy.com" />
              </FooterSection>
            </MotionBox>
          </MotionBox>

          <Separator
            w="full"
            mt={{ base: "2rem", tablet: "2.25rem", desktop: "2.5rem" }}
            mb={{ base: "1.25rem", tablet: "1.5rem" }}
          />

          {/* Bottom Bar */}
          <MotionFlex
            variants={bottomVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            w="full"
            direction={{ base: "column", tablet: "row" }}
            align="center"
            justify={{ base: "center", tablet: "space-between" }}
            gap={{ base: "0.6rem", tablet: "0" }}
            textAlign="center"
          >
            <Text
              textStyle="bodySmall.base"
              color="greyscale.text.body"
              opacity={0.85}
            >
              © 2026 HomeHelpy. All rights reserved.
            </Text>

            <Flex gap="1.25rem" direction="row" align="center">
              <TCLink href="/terms">Terms & Conditions</TCLink>
              <TCLink href="/privacy-policy">Privacy Policy</TCLink>
            </Flex>
          </MotionFlex>
        </Flex>
      </Flex>
    </Box>
  );
}

/* Reusable Components */

function FooterSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <List.Root
      listStyle="none"
      gap={{ base: "0.8rem", desktop: "0.95rem" }}
      textAlign="left"
      alignItems="stretch"
    >
      <Heading
        textStyle={{ base: "bodyLarge.strong", desktop: "h5Bold" }}
        color="primary.text.label"
        mb={{ base: "0.15rem", desktop: "0.35rem" }}
      >
        {title}
      </Heading>
      {children}
    </List.Root>
  );
}

function StyledFooterLink({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  return (
    <RouterLink
      to={to}
      style={{ textDecoration: "none", display: "inline-block" }}
    >
      <Text
        textStyle="bodyRegular.base"
        color="greyscale.text.body"
        transition="color 0.22s ease, transform 0.22s ease"
        _hover={{
          color: "primary.surface.default",
          transform: "translateY(-2px)",
        }}
      >
        {children}
      </Text>
    </RouterLink>
  );
}

function TCLink(props: LinkProps) {
  return (
    <Link
      {...props}
      textStyle="bodySmall.base"
      color="greyscale.text.body"
      position="relative"
      textDecor="none"
      _after={{
        content: '""',
        position: "absolute",
        left: 0,
        bottom: "-2px",
        width: "100%",
        height: "1px",
        bg: "greyscale.text.body",
        opacity: 0,
        transition: "opacity 0.25s ease",
      }}
      _hover={{ textDecor: "none", _after: { opacity: 1 } }}
    />
  );
}

function FooterContact({ icon, text }: { icon: ElementType; text: string }) {
  return (
    <HStack align="flex-start" justify="flex-start" gap="0.65rem" w="full">
      <Icon
        as={icon}
        opacity={0.78}
        boxSize={4.5}
        mt="0.15rem"
        flexShrink={0}
      />
      <Text
        whiteSpace="pre-line"
        wordBreak="break-word"
        textStyle="bodyRegular.base"
        color="greyscale.text.body"
        lineHeight="1.55"
      >
        {text}
      </Text>
    </HStack>
  );
}

function SocialLink({
  href,
  icon,
  label,
}: {
  href: string;
  icon: ElementType;
  label: string;
}) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      _hover={{ transform: "scale(1.15)" }}
      transition="0.25s"
    >
      <Icon
        as={icon}
        boxSize={6}
        color="primary.text.label"
        _hover={{ color: "primary.surface.default" }}
        transition="0.25s"
      />
    </Link>
  );
}
